import { doc, setDoc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase/config';
import { deleteSeriesFromFirebase } from './seriesService';

/**
 * Add a new hero to Firebase
 */
export const addHeroToFirebase = async (hero, userEmail) => {
  const now = new Date().toISOString();

  // Ensure all required fields are present
  const enhancedHero = {
    ...hero,
    id: hero.id !== undefined ? hero.id : null,
    source: hero.source || 'custom',
    locked: hero.locked || false,
    createDate: hero.createDate || now,
    modified: now,
    publisher: hero.publisher || 'Unknown',
    resourceURI: hero.resourceURI || '',
    series: hero.series || { available: 0, items: [] },
    stories: hero.stories || { available: 0, items: [] },
    urls: hero.urls || [],
    resourceType: hero.resourceType || 'character' // Default to 'character' for backward compatibility
  };

  const docRef = doc(firestore, "heroes/" + enhancedHero.name);
  await setDoc(docRef, { name: enhancedHero.name, data: enhancedHero });

  const userData = doc(firestore, "users/" + userEmail + "/heroes/" + enhancedHero.name);
  await setDoc(userData, { name: enhancedHero.name, data: enhancedHero });

  return enhancedHero;
};

/**
 * Update an existing hero in Firebase
 */
export const updateHeroInFirebase = async (hero, userEmail) => {
  if (!hero || !hero.name) {
    console.error('updateHeroInFirebase: invalid hero', hero);
    return;
  }

  if (!userEmail) {
    console.error('updateHeroInFirebase: no user email - cannot update Firestore');
    return;
  }

  const now = new Date().toISOString();
  const updatedHero = {
    ...hero,
    id: hero.id !== undefined ? hero.id : null,
    source: hero.source || 'custom',
    locked: hero.locked || false,
    createDate: hero.createDate || hero.modified || now,
    modified: now,
    publisher: hero.publisher || 'Unknown',
    resourceURI: hero.resourceURI || '',
    series: hero.series || { available: 0, items: [] },
    stories: hero.stories || { available: 0, items: [] },
    urls: hero.urls || [],
    resourceType: hero.resourceType || 'character' // Default to 'character' for backward compatibility
  };

  const docRef = doc(firestore, "heroes/" + updatedHero.name);
  await setDoc(docRef, { name: updatedHero.name, data: updatedHero });

  const userData = doc(firestore, "users/" + userEmail + "/heroes/" + updatedHero.name);
  await setDoc(userData, { name: updatedHero.name, data: updatedHero });

  return updatedHero;
};

/**
 * Delete a hero from Firebase
 */
export const deleteHeroFromFirebase = async (hero, userEmail) => {
  const userData = doc(firestore, "users/" + userEmail + "/heroes/" + hero.name);

  // Delete all series first
  const seriesCollection = collection(firestore, "users/" + userEmail + "/heroes/" + hero.name + "/series");
  const querySnapshot = await getDocs(seriesCollection);

  const promises = querySnapshot.docs.map(seriesDoc => {
    const seriesData = seriesDoc.data();
    // Create a card object from the series document
    const seriesCard = {
      title: seriesData.title || seriesDoc.id,
      ...seriesData.data
    };
    return deleteSeriesFromFirebase(seriesCard, hero, userEmail);
  });

  await Promise.all(promises);
  await deleteDoc(userData);
};

/**
 * Merge hero data from Comic Vine with existing hero data
 */
export const mergeHeroData = (originalHero, comicVineHero) => {
  const now = new Date().toISOString();

  return {
    ...originalHero,
    id: originalHero.id || comicVineHero.id,
    comicVineId: comicVineHero.comicVineId,
    comicVineImage: comicVineHero.comicVineImage,
    description: comicVineHero.description || originalHero.description,
    thumbnail: comicVineHero.thumbnail || originalHero.thumbnail,
    publisher: comicVineHero.publisher || originalHero.publisher || 'Unknown',
    createDate: originalHero.createDate || originalHero.modified || now,
    modified: now,
    resourceURI: originalHero.resourceURI || comicVineHero.resourceURI || '',
    series: originalHero.series || comicVineHero.series || { available: 0, items: [] },
    stories: originalHero.stories || comicVineHero.stories || { available: 0, items: [] },
    urls: originalHero.urls || comicVineHero.urls || [],
    source: originalHero.source || comicVineHero.source || 'comicvine',
    resourceType: comicVineHero.resourceType || originalHero.resourceType || 'character'
  };
};


